import classNames from "classnames";
import { AuditLogsIcon } from "components/ui/Icons/AuditLogsIcon";
import { BundleIcon } from "components/ui/Icons/BundleIcon";
import { InProgressIcon } from "components/ui/Icons/InProgressIcon";
import { IntegrationIcon } from "components/ui/Icons/IntegrationIcon";
import { OverviewIcon } from "components/ui/Icons/OverviewIcon";
import { PermissionsIcon } from "components/ui/Icons/PermissionsIcon";
import { PoliciesIcon } from "components/ui/Icons/PoliciesIcon";
import { ReportIcon } from "components/ui/Icons/ReportIcon";
import { RequestsIcon } from "components/ui/Icons/RequestsIcon";
import { SettingsIcon } from "components/ui/Icons/SettingsIcon";
import { TaskIcon } from "components/ui/Icons/TaskIcon";
import { UsersIcon } from "components/ui/Icons/UsersIcon";
import { WorkflowsIcon } from "components/ui/Icons/WorkflowsIcon";
import { useSideNavContext } from "context/sideNavContext";
import { useAuthenticatedUser } from "hooks/useAuthenticatedUser";
import { useBreakpoints } from "hooks/useBreakpoints";
import { useSideNavVisibility } from "hooks/useSideNavVisibility";
import { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Bp } from "utils/ui/breakpoints";

import { Section } from "./components/Section";
import { Spacer } from "./components/Spacer";
import { TabOption } from "./components/TabOption";
import { ToggleSection } from "./components/ToggleSection";
import { useStyles } from "./styles";

export const SideNav: FC = ({ className }) => {
  const { t } = useTranslation();
  const { user } = useAuthenticatedUser();
  const classes = useStyles();
  const { isBiggerThan } = useBreakpoints(Bp.FORTY);
  const [minimized, setMinimized] = useState(false);

  // eslint-disable-next-line react-hooks/set-state-in-effect -- Legacy
  useEffect(() => setMinimized(!isBiggerThan), [isBiggerThan]);

  const toggle = useCallback(() => setMinimized((curr) => !curr), []);

  const {
    state: { hideSideNav },
  } = useSideNavContext();

  const {
    hasAnyPageAccess,
    isAuthorizationResolved,
    showAccessReviewSection,
    showAdministrativeSection,
    showReporting,
  } = useSideNavVisibility();

  if (hideSideNav || !user || (isAuthorizationResolved && !hasAnyPageAccess)) return null;

  return (
    <div className={classNames(classes.navigationBar, { [classes.minimized]: minimized }, className)} data-public>
      {isBiggerThan ? <ToggleSection toggle={toggle} /> : null}

      <div className={classes.scrollContainer}>
        {isAuthorizationResolved && (
          <>
            <Section minimized={minimized}>
              {showReporting && (
                <TabOption
                  Icon={ReportIcon}
                  minimized={minimized}
                  routeKey="reporting"
                  text={t("navigation.reporting")}
                />
              )}
              <TabOption
                Icon={RequestsIcon}
                minimized={minimized}
                routeKey="requests"
                text={t("navigation.requests")}
              />

              <TabOption
                Icon={PermissionsIcon}
                minimized={minimized}
                routeKey="identityGraph"
                text={t("navigation.identityGraph")}
              />

              <TabOption
                Icon={WorkflowsIcon}
                minimized={minimized}
                routeKey="workflows"
                text={t("navigation.workflows")}
              />

              <TabOption
                Icon={IntegrationIcon}
                minimized={minimized}
                routeKey="integrations"
                text={t("navigation.integrations")}
              />

              <TabOption Icon={BundleIcon} minimized={minimized} routeKey="bundles" text={t("navigation.bundles")} />

              <TabOption
                Icon={PoliciesIcon}
                minimized={minimized}
                routeKey="policies"
                text={t("navigation.policies")}
              />

              <TabOption Icon={TaskIcon} minimized={minimized} routeKey="tasks" text={t("navigation.tasks")} />
            </Section>

            {showAccessReviewSection && (
              <>
                <Spacer />

                <Section header={t("navigation.accessReviewHeader")} minimized={minimized}>
                  <TabOption
                    Icon={OverviewIcon}
                    minimized={minimized}
                    routeKey="accessReview"
                    text={t("navigation.accessReview")}
                  />

                  <TabOption
                    Icon={ReportIcon}
                    minimized={minimized}
                    routeKey="accessReport"
                    text={t("navigation.accessReport")}
                  />
                </Section>
              </>
            )}

            {showAdministrativeSection && (
              <>
                <Spacer />

                <Section header={t("navigation.administrationHeader")} minimized={minimized}>
                  <TabOption
                    Icon={InProgressIcon}
                    minimized={minimized}
                    routeKey="requestsLog"
                    text={t("navigation.requestsLog")}
                  />

                  <TabOption
                    Icon={AuditLogsIcon}
                    minimized={minimized}
                    routeKey="auditLogs"
                    text={t("navigation.auditLogs")}
                  />

                  <TabOption
                    Icon={SettingsIcon}
                    minimized={minimized}
                    routeKey="settings"
                    text={t("navigation.settings")}
                  />

                  <TabOption Icon={UsersIcon} minimized={minimized} routeKey="users" text={t("navigation.users")} />
                </Section>
              </>
            )}
          </>
        )}
      </div>
    </div>
  );
};
